// =====================================================================================
// GameInstanceContext - Proper React Context for Phaser Game Management
// =====================================================================================
// Replaces the window.phaserGame anti-pattern with proper React context management
// Provides type-safe access to the Phaser game instance throughout the component tree

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// =====================================================================================
// TYPE DEFINITIONS
// =====================================================================================

/**
 * Phaser game instance interface (simplified)
 * This matches the expected structure from Phaser.Game
 */
interface GameInstance {
  scene: {
    getScene: (key: string) => any;
    /** Active scenes (Phaser.Scenes.SceneManager#getScenes); each has its own KeyboardPlugin */
    getScenes?: (isActive?: boolean) => Array<{
      input?: { keyboard?: { resetKeys?: () => void } | null } | null;
    }>;
  };
  /** Global input manager; `keyboard` is the KeyboardManager (null if disabled in config) */
  input?: {
    enabled?: boolean;
    keyboard?: { enabled: boolean } | null;
  } | null;
  destroy: (removeCanvas?: boolean) => void;
  // Add other Phaser.Game properties as needed
}

/**
 * Game scene interface (simplified)
 * This matches the expected structure of our GameScene
 */
interface GameScene {
  updateZoneData?: (zones: any[]) => void;
  updatePrivateAreas?: (areas: any[]) => void;
  refreshPlayerNameTag?: (userId: string, status: string) => void;
  handlePrivateAreaUpdate?: (data: any) => void;
  getSpawnPoint?: () => { x: number; y: number } | undefined;
  systems?: {
    privateAreas?: {
      handleDoorUpdate: (areaId: string, isDoorOpen: boolean) => void;
    };
  };
  roomManager?: {
    handleTeleportUpdate: (userId: string, position: { x: number; y: number }) => void;
    initializeWithSocket: (socket: any) => void;
    updatePlayers: (players: any[], currentUserId: string, updateType: string) => void;
    initializeLocalPlayer: (playerData: any, roomId: string) => void;
    isInitialized: boolean;
    socketId: string;
    cleanup: () => void;
  };
  initializeRoomWorld?: (roomId: string) => Promise<void>;
  initializePrivateAreaSystem?: (socket: any, userId: string) => void;
  registry?: {
    get: (key: string) => any;
    set: (key: string, value: any) => void;
    remove: (key: string) => void;
  };
  /** Scene systems; an invisible scene keeps updating but is not rendered */
  sys?: {
    isVisible?: () => boolean;
    setVisible?: (visible: boolean) => unknown;
  };
  player?: any;
}

/**
 * Game context value interface
 */
interface GameContextValue {
  /** The Phaser game instance (null if not created) */
  gameInstance: GameInstance | null;
  /** Whether the game is ready for operations */
  isGameReady: boolean;
  /** Set the game instance (used by useRoomConnection) */
  setGameInstance: (game: GameInstance | null) => void;
  /** Set game ready status (used by useRoomConnection) */
  setIsGameReady: (ready: boolean) => void;
  /** Get the main game scene safely */
  getGameScene: () => GameScene | null;
  /** Check if game instance exists and is ready */
  isReady: () => boolean;
}

// =====================================================================================
// CONTEXT CREATION
// =====================================================================================

const GameInstanceContext = createContext<GameContextValue | null>(null);

// =====================================================================================
// CONTEXT PROVIDER
// =====================================================================================

/**
 * GameInstanceProvider Props
 */
interface GameInstanceProviderProps {
  children: ReactNode;
}

/**
 * GameInstanceProvider - Provides Phaser game instance to the component tree
 * 
 * This provider manages the Phaser game instance lifecycle and provides
 * type-safe access to the game and its scenes throughout the React app.
 * 
 * @param props - Provider props with children
 * @returns Provider component wrapping children
 */
export function GameInstanceProvider({ children }: GameInstanceProviderProps) {
  const [gameInstance, setGameInstance] = useState<GameInstance | null>(null);
  const [isGameReady, setIsGameReady] = useState(false);

  /**
   * Get the main game scene safely
   * @returns GameScene instance or null if not available
   */
  const getGameScene = useCallback((): GameScene | null => {
    if (!gameInstance || !isGameReady) {
      return null;
    }
    
    try {
      return gameInstance.scene.getScene('scene-game') || null;
    } catch (error) {
      console.warn('GameInstanceContext: Error accessing game scene:', error);
      return null;
    }
  }, [gameInstance, isGameReady]);

  /**
   * Check if game instance exists and is ready
   * @returns True if game is ready for operations
   */
  const isReady = useCallback((): boolean => {
    return !!(gameInstance && isGameReady);
  }, [gameInstance, isGameReady]);

  const contextValue: GameContextValue = {
    gameInstance,
    isGameReady,
    setGameInstance,
    setIsGameReady,
    getGameScene,
    isReady
  };

  return (
    <GameInstanceContext.Provider value={contextValue}>
      {children}
    </GameInstanceContext.Provider>
  );
}

// =====================================================================================
// CONTEXT HOOK
// =====================================================================================

/**
 * useGameInstance Hook - Access Phaser game instance via React context
 * 
 * This hook provides type-safe access to the Phaser game instance and its scenes.
 * It replaces direct window.phaserGame access with proper React patterns.
 * 
 * @returns Game context with instance, ready state, and helper methods
 * @throws Error if used outside GameInstanceProvider
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { gameInstance, getGameScene, isReady } = useGameInstance();
 *   
 *   const handleAction = () => {
 *     if (isReady()) {
 *       const scene = getGameScene();
 *       scene?.updateZoneData(zones);
 *     }
 *   };
 *   
 *   return <button onClick={handleAction}>Update Game</button>;
 * }
 * ```
 */
export function useGameInstance(): GameContextValue {
  const context = useContext(GameInstanceContext);
  
  if (!context) {
    throw new Error('useGameInstance must be used within a GameInstanceProvider');
  }
  
  return context;
}

// =====================================================================================
// UTILITY HOOKS
// =====================================================================================

/**
 * useGameScene Hook - Direct access to the main game scene
 * 
 * Convenience hook that directly returns the game scene if available.
 * 
 * @returns GameScene instance or null if not ready
 * 
 * @example
 * ```tsx
 * function GameComponent() {
 *   const gameScene = useGameScene();
 *   
 *   useEffect(() => {
 *     if (gameScene) {
 *       gameScene.updateZoneData(zones);
 *     }
 *   }, [gameScene, zones]);
 *   
 *   return <div>Game Component</div>;
 * }
 * ```
 */
export function useGameScene(): GameScene | null {
  const { getGameScene } = useGameInstance();
  return getGameScene();
}

/**
 * useGameReady Hook - Check if game is ready for operations
 * 
 * Convenience hook for components that need to know if the game is ready.
 * 
 * @returns True if game instance exists and is ready
 * 
 * @example
 * ```tsx
 * function GameDependentComponent() {
 *   const isGameReady = useGameReady();
 *   
 *   if (!isGameReady) {
 *     return <div>Loading game...</div>;
 *   }
 *   
 *   return <div>Game is ready!</div>;
 * }
 * ```
 */
export function useGameReady(): boolean {
  const { isReady } = useGameInstance();
  return isReady();
}